/**
 * COA Request Service for Hilton Analytica COA Platform
 * 
 * Handles submission of COA (Certificate of Analysis) requests to the backend Lambda.
 * Provides retry logic, timeout handling, and error management.
 */

import {
  SubmissionResponse,
  LambdaErrorResponse,
  RequestOptions,
} from '../types';
import { BaseApiService, ApiServiceError } from './baseApiService';

/**
 * Input data for COA request submission
 * Omits auto-generated fields (id, status, submittedAt) that are set by the backend
 */
export interface COASubmissionData {
  companyName: string;
  contactName: string;
  email: string;
  phone?: string;
  samples: Array<{
    productName: string;
    lotNumber: string;
    quantity: number;
    tests: {
      purityAndId: boolean;
      netContent: boolean;
      endotoxins: boolean;
      sterility: boolean;
      conformity: boolean;
      heavyMetals: boolean;
      fentanyl: boolean;
      publishToPublic: boolean;
    };
  }>;
  shipping: {
    streetAddress: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
    specialInstructions?: string;
  };
}

/**
 * Configuration for the submit-request Lambda endpoint
 */
const SUBMIT_REQUEST_ENDPOINT = '/submit-request';
const SUBMIT_REQUEST_TIMEOUT = 30000; // 30 seconds as per requirement 2.3

/**
 * Default options for COA request submissions
 */
const DEFAULT_SUBMIT_OPTIONS: RequestOptions = {
  timeout: SUBMIT_REQUEST_TIMEOUT,
  retries: 3,
  retryDelay: 1000,
};

/**
 * COA Request Service class for submitting COA requests
 */
export class COARequestService {
  private apiService: BaseApiService;

  constructor(apiService?: BaseApiService) {
    // Initialize with default configuration
    this.apiService = apiService || new BaseApiService('', DEFAULT_SUBMIT_OPTIONS);
  }

  /**
   * Submit a COA request to the Lambda endpoint
   * 
   * @param submissionData - COA request data including company info, contact details, and product info
   * @returns Promise resolving to SubmissionResponse with requestId, message, and submittedAt
   * @throws ApiServiceError if the request fails after all retry attempts or receives 4xx/5xx error
   * 
   * Features:
   * - 30 second timeout (requirement 2.3)
   * - Retry logic up to 3 attempts (requirement 2.5)
   * - Handles 2xx success responses (requirement 2.4)
   * - Handles 4xx/5xx error responses (requirement 2.5)
   */
  async submitCOARequest(submissionData: COASubmissionData): Promise<SubmissionResponse> {
    try {
      // Make POST request to submit-request Lambda endpoint
      const response = await this.apiService.post<SubmissionResponse>(
        SUBMIT_REQUEST_ENDPOINT,
        submissionData,
        DEFAULT_SUBMIT_OPTIONS
      );

      // Handle successful 2xx response (requirement 2.4)
      if (response.success) {
        return response.data;
      }

      // Handle error response from Lambda (requirement 2.5)
      const errorResponse = response as LambdaErrorResponse;
      throw new ApiServiceError(
        errorResponse.error || 'Failed to submit COA request',
        500,
        errorResponse.code
      );
    } catch (error) {
      if (error instanceof ApiServiceError) {
        throw error;
      }
      throw new ApiServiceError(
        error instanceof Error ? error.message : 'Unknown error during COA request submission',
        undefined,
        'SUBMISSION_ERROR'
      );
    }
  }
}

// Export default instance
export const coaRequestService = new COARequestService();

/**
 * Convenience function to submit a COA request
 * 
 * @param submissionData - COA request data
 * @returns Promise resolving to SubmissionResponse
 */
export async function submitCOARequest(submissionData: COASubmissionData): Promise<SubmissionResponse> {
  return coaRequestService.submitCOARequest(submissionData);
}